/**
 * Utilities for translating Samsara compass headings into
 * dispatcher-friendly direction labels (NB/SB/EB/WB) and parsing
 * highway names from reverse-geocoded location strings.
 */

export type HighwayDirection = "NB" | "SB" | "EB" | "WB";

/**
 * Convert a heading in degrees (0 = N, 90 = E, 180 = S, 270 = W) to a
 * highway-relative direction. Uses 90° quadrants centered on each
 * cardinal axis: NE quadrant (315–45) → NB, etc.
 */
export function degreesToHighwayDirection(deg: number | null | undefined): HighwayDirection | null {
  if (deg == null || Number.isNaN(deg)) return null;
  const norm = ((deg % 360) + 360) % 360;
  if (norm >= 315 || norm < 45) return "NB";
  if (norm >= 45 && norm < 135) return "EB";
  if (norm >= 135 && norm < 225) return "SB";
  return "WB";
}

/**
 * Convert a Samsara cardinal label (N, NE, E, SE, S, SW, W, NW) to
 * highway direction. Used as a fallback when raw degrees are unavailable.
 */
export function cardinalToHighwayDirection(cardinal: string | null | undefined): HighwayDirection | null {
  if (!cardinal) return null;
  const c = cardinal.toUpperCase().trim();
  if (c === "N" || c === "NE" || c === "NW") return "NB";
  if (c === "S" || c === "SE" || c === "SW") return "SB";
  if (c === "E") return "EB";
  if (c === "W") return "WB";
  return null;
}

/**
 * Extract a highway designator (e.g. "I-20", "I-285", "US-78", "GA-400")
 * from a reverse-geocoded city/road string. Returns null if no highway
 * token is present.
 */
export function parseHighwayFromCity(city: string | null | undefined): string | null {
  if (!city) return null;
  // Match patterns like I-20, I 285, US-78, US 41, GA-400, SR-6, Hwy 78
  const re = /\b(I[- ]?\d{1,3}|US[- ]?\d{1,3}|[A-Z]{2}[- ]?\d{1,3}|HWY[- ]?\d{1,3}|SR[- ]?\d{1,3})\b/i;
  const m = city.match(re);
  if (!m) return null;
  // Normalize "I 20" / "I20" → "I-20"
  return m[1].toUpperCase().replace(/\s+/g, "-").replace(/^([A-Z]+)(\d)/, "$1-$2");
}

/**
 * Build a dispatcher-friendly route label, e.g. "I-20 EB" or just "EB"
 * when no highway can be parsed from the location string.
 */
export function formatRouteDirection(
  city: string | null | undefined,
  headingDeg: number | null | undefined,
  cardinalFallback?: string | null
): string | null {
  const dir = degreesToHighwayDirection(headingDeg) ?? cardinalToHighwayDirection(cardinalFallback);
  if (!dir) return null;
  const hwy = parseHighwayFromCity(city);
  return hwy ? `${hwy} ${dir}` : dir;
}
